import {take, takeRight} from 'lodash';
import React, {FC, Fragment, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {globalWindow} from '../../../../libs/shared/helpers/src/browser/browserHelpers';
import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {KeyboardEventHandlers} from '../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useSize} from '../../../../libs/shared/ui/src/hooks/sizeHooks';
import {usePush} from '../../../../srcCommon/push/pushContext';
import {useAppDispatch, useAppSelector} from '../../../helpers/reduxHelpers';
import {CompanyFeaturesEnum} from '../../../models/api/companyModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {getFindHasCompanyFeature} from '../../../store/data/company/companySelectors';
import {getHasUpsellCards} from '../../../store/featureFlags/featureFlagsSelectors';
import {pushContentTypeAction} from '../../../store/panels/app/appPanelRouteActions';
import {AppPanelId} from '../../../store/panels/panelStateBase';
import {DropdownItemIcon} from '../../common/dropdowns/dropdownItemIcon';
import {TooltipLabelWithShortcut} from '../../common/tooltips/tooltipLabelWithShortcut';
import {PlanExceededBanner} from './planExceededBanner';
import {TopBarButton} from './topBarButton';
import {AppDefinition, TrackingLocationsEnum} from './topBarHelpers';

/*
 * Constants.
 */

const maxVisibleApps = 5;

export const appSwitcherButtonWidth = 32;
const appSwitcherButtonMargin = 15;

// Features we want to upsell companies on lower plans.
export const upsellCardsFeatures = [CompanyFeaturesEnum.ANALYTICS, CompanyFeaturesEnum.SEQUENCES];

/*
 * Props.
 */

interface AppSwitcherProps {
  panelId: AppPanelId;
  apps: ReadonlyArray<AppDefinition>;
  selectedApp?: AppDefinition;
}

/*
 * Styles.
 */

const StyledAppSwitcherWrapper = styled.div`
  -webkit-app-region: drag;

  grid-area: apps;
  overflow-x: hidden;
`;

const StyledAppSwitcher = styled.div<{$maxWidth: number}>`
  max-width: ${(p) => p.$maxWidth}px;

  display: flex;
`;

const StyledTopBarButton = styled(TopBarButton)`
  margin-right: ${appSwitcherButtonMargin}px;
`;

const StyledDropdown = styled(Dropdown)`
  margin-top: 4px;
`;

/*
 * Component.
 */

export const AppSwitcher: FC<AppSwitcherProps> = ({panelId, apps, selectedApp}) => {
  const {isConnected} = usePush();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const wrapperSize = useSize(wrapperRef);
  const wrapperWidth = wrapperSize?.width ?? 0;

  const [planExceededFeature, setPlanExceededFeature] = useState<CompanyFeaturesEnum>();

  // Dismiss the last banner after 5s.
  useEffect(() => {
    if (!planExceededFeature) {
      return undefined;
    }

    const timeout = globalWindow.setInstrumentedTimeout(() => setPlanExceededFeature(undefined), 5 * 1000);
    return () => globalWindow.clearTimeout(timeout);
  }, [planExceededFeature]);

  const hasUpsellCards = useAppSelector(getHasUpsellCards);
  const findHasCompanyFeature = useAppSelector(getFindHasCompanyFeature);

  // Find how many can be displayed, and whether we need an overflow menu.
  // @NOTE: This number takes the overflow button into account!
  const visibleAppsCount = Math.min(
    maxVisibleApps,
    apps.length,
    Math.floor(wrapperWidth / (appSwitcherButtonWidth + appSwitcherButtonMargin)),
  );
  const maxWidth = visibleAppsCount * (appSwitcherButtonWidth + appSwitcherButtonMargin);

  const requiresOverflow = apps.length > visibleAppsCount - 1;
  const visibleAppsFromListCount = requiresOverflow ? visibleAppsCount - 1 : visibleAppsCount;

  // Break down the apps into visible and overflow.
  const visibleApps = take(apps, visibleAppsFromListCount);
  const overflowApps = takeRight(apps, apps.length - visibleAppsFromListCount);

  const dispatch = useAppDispatch();

  const onAppButtonClick = (app: AppDefinition) => {
    // If the user has the upsell cards feature flag, we should allow them to navigate to
    // analytics and sequences even if they do not have access to those features. Once they
    // open those pages, they'll see an upsell card.
    const shouldAllowNavigationToUpsell =
      hasUpsellCards && app.feature && upsellCardsFeatures.includes(app.feature);

    if (app.feature && !findHasCompanyFeature(app.feature) && !shouldAllowNavigationToUpsell) {
      setPlanExceededFeature(app.feature);

      return;
    }

    // @TODO: Use PanelTypesEnum when P0 is fully rolled out.
    trackInterfaceEvent(InterfaceEventsEnum.NAVIGATION_CLICK, {
      object_name: app.path.substring(1),
      location: TrackingLocationsEnum.P0,
    });

    dispatch(pushContentTypeAction(panelId, app.contentType));
  };

  const visibleAppsRender = visibleApps.map((app) => {
    const isSelected = app === selectedApp;

    return renderTopBarButton(app, isSelected, isConnected, onAppButtonClick);
  });

  const keyboardActions = apps.reduce((acc, app) => {
    if (!app.keyboardAction) {
      return acc;
    }

    return {
      ...acc,
      [app.keyboardAction]: () => onAppButtonClick(app),
    };
  }, {} as KeyboardEventHandlers);

  return (
    <>
      <KeyboardShortcuts handlers={keyboardActions}>
        <StyledAppSwitcherWrapper ref={wrapperRef}>
          <StyledAppSwitcher $maxWidth={maxWidth}>
            {visibleAppsRender}
            {requiresOverflow && renderOverflow(overflowApps, selectedApp, onAppButtonClick, !isConnected)}
          </StyledAppSwitcher>
        </StyledAppSwitcherWrapper>
      </KeyboardShortcuts>
      {planExceededFeature && <PlanExceededBanner key={planExceededFeature} feature={planExceededFeature} />}
    </>
  );
};

/*
 * Helpers.
 */

const renderTopBarButton = (
  app: AppDefinition,
  isSelected: boolean,
  isConnected: boolean,
  onAppButtonClick: HandlerOf<AppDefinition>,
  shouldDisableOnClick?: boolean,
) => (
  <StyledTopBarButton
    key={app.iconName}
    iconName={app.iconName}
    isSelected={isSelected}
    renderTooltip={() => renderButtonTooltip(app, isSelected, isConnected)}
    onClick={!shouldDisableOnClick ? () => onAppButtonClick(app) : undefined}
    isDisabled={!isConnected}
    interactionId={app.contentPanelType && `p0-app-switcher-${app.contentPanelType.toLowerCase()}`}
  >
    {app.title}
  </StyledTopBarButton>
);

const renderButtonTooltip = (app: AppDefinition, isSelected: boolean, isConnected: boolean) => {
  if (!isConnected && !isSelected) {
    return <Trans id="tab-offline-tooltip">Not available offline</Trans>;
  }

  return <TooltipLabelWithShortcut label={app.title} keyboardAction={app.keyboardAction} />;
};

const renderOverflow = (
  overflowApps: ReadonlyArray<AppDefinition>,
  selectedApp: AppDefinition | undefined,
  onAppClick: HandlerOf<AppDefinition>,
  isOffline: boolean,
) => {
  if (isOffline) {
    return renderOverflowButton(overflowApps, selectedApp, {isOpen: false, isOffline: true});
  }

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => renderOverflowButton(overflowApps, selectedApp, {isOpen, isOffline: false})}
      renderModal={(popoverProps) =>
        renderOverflowDropdown(popoverProps, overflowApps, selectedApp, onAppClick)
      }
    />
  );
};

const renderOverflowButton = (
  overflowApps: ReadonlyArray<AppDefinition>,
  selectedApp: AppDefinition | undefined,
  {isOpen, isOffline}: {isOpen: boolean; isOffline: boolean},
) => (
  <StyledTopBarButton
    iconName="moreVertical"
    renderTooltip={() => renderOverflowButtonTooltip(overflowApps)}
    isSelected={selectedApp && overflowApps.includes(selectedApp)}
    hasHover={isOpen}
    isDisabled={isOffline}
  >
    <Trans id="tab-overflow">More applications</Trans>
  </StyledTopBarButton>
);

const renderOverflowButtonTooltip = (apps: ReadonlyArray<AppDefinition>) =>
  apps.map((app, index) => (
    <Fragment key={app.iconName}>
      {index > 0 && ', '}
      {app.title}
    </Fragment>
  ));

const renderOverflowDropdown = (
  popoverProps: PopoverTransferProps,
  overflowApps: ReadonlyArray<AppDefinition>,
  selectedApp: AppDefinition | undefined,
  onAppClick: HandlerOf<AppDefinition>,
) => (
  <StyledDropdown {...popoverProps}>
    <DropdownList>
      {overflowApps.map((app) => (
        <DropdownItem
          key={app.iconName}
          isChecked={app === selectedApp}
          onSelect={() => {
            onAppClick(app);
            // Close the dropdown.
            popoverProps.onRequestClose();
          }}
          shouldReserveRightIcon={Boolean(selectedApp && overflowApps.includes(selectedApp))}
        >
          <DropdownItemIcon label={null} iconName={app.iconName} />
          <DropdownItemContent>{app.title}</DropdownItemContent>
        </DropdownItem>
      ))}
    </DropdownList>
  </StyledDropdown>
);
